/**
 * The ONE place the client calls the Firebase SDK's `httpsCallable` / `httpsCallableFromURL`.
 *
 * Why it exists: Firebase Auth restores the signed-in user asynchronously after a page load. A callable fired before that finishes (a deep link
 * that renders a page whose hooks fire on mount) is sent with NO ID token, and the server answers 401 even though the user is signed in. The
 * measured case was a manager opening a deep link and getting 8 unauthenticated callables at once. These wrappers hold a call until
 * `auth.authStateReady()` has resolved. For a signed-out visitor it also resolves (it means "auth finished starting", not "a user exists"), so
 * public callables (public forms) are held only for that start-up moment and never blocked.
 *
 * Same signature as the SDK functions, so call sites change only their import. `client/.eslintrc.json` bans importing the SDK names anywhere
 * else (`no-restricted-imports`), so a new call site cannot reintroduce the race.
 *
 * HOW AUTH IS KNOWN: config/firebase.ts calls `registerAuthReady(() => auth.authStateReady())` once, right after it creates `auth`. This module
 * imports nothing from config/firebase so it can be loaded (and mocked) without initialising Firebase. Before anything is registered (unit tests
 * that mock config/firebase, or a module loaded without it) calls are NOT held; in the app, config/firebase is always loaded first because
 * every call site gets its `functions` instance from it.
 *
 * SECOND GUARD, the token (OFFICEHUB-CLIENT-8E): the SDK catches ANY token-fetch error and sends the call with no Authorization header. So while a
 * user is signed in, every call first fetches the ID token itself and, if that throws (a long-idle tab whose refresh failed), refuses with a
 * retryable AuthTokenUnavailableError instead of a tokenless send. A server 'unauthenticated' with no details.reason is retried ONCE after a forced refresh (safe: a
 * missing-token 401 is rejected before any handler runs; a handler's own 'unauthenticated' carries details.reason and is never retried). A signed-out visitor is never asked for a token (public forms).
 *
 * Named limits: a rejected `authStateReady()`, or one that has not settled after AUTH_READY_TIMEOUT_MS, releases the held calls (they go out as they
 * did before this wrapper; the server decides) and logs one warning. Holding applies to the FIRST calls only. For a signed-out visitor, or with no
 * token source registered, calls then take the synchronous path; a signed-in call always awaits its token. `.stream` gets the token guarantee but no retry.
 */
import {
  httpsCallable as sdkHttpsCallable,
  httpsCallableFromURL as sdkHttpsCallableFromURL,
  type Functions,
  type HttpsCallable,
  type HttpsCallableOptions,
} from 'firebase/functions';
import { annotateCallableFailure } from '../telemetry/callableFailureContext';

type AuthReady = () => Promise<unknown>;

/**
 * Where the wrapper gets the signed-in user's ID token. The Firebase SDK swallows ANY token-fetch error and sends the call with no
 * Authorization header (the 401 burst on a long-idle tab whose token refresh failed), so the wrapper fetches the token itself first and refuses
 * to send when it cannot. `hasUser` false = a signed-out visitor: public callables go out as they always did.
 */
export interface TokenSource {
  hasUser: () => boolean;
  /** The signed-in user's uid, or null. Compared before a retry so a call made as one user is never re-sent as another. */
  uid: () => string | null;
  getIdToken: (forceRefresh?: boolean) => Promise<string>;
}

/** The slice of a Firebase `Auth` the wrapper needs. config/firebase.ts builds its TokenSource from the real `auth` with this, so the wiring is testable. */
export interface AuthLike {
  currentUser: { uid: string; getIdToken: (forceRefresh?: boolean) => Promise<string> } | null;
}

/** @param {AuthLike} auth the Firebase Auth instance. @return {TokenSource} the token source over its current user. */
export function tokenSourceFromAuth(auth: AuthLike): TokenSource {
  return {
    hasUser: () => auth.currentUser != null,
    uid: () => auth.currentUser?.uid ?? null,
    getIdToken: (forceRefresh) => {
      const user = auth.currentUser;
      return user ? user.getIdToken(forceRefresh) : Promise.reject(new Error('no signed-in user'));
    },
  };
}

/**
 * Thrown instead of a tokenless send. `code` is the SDK's 'functions/unavailable', so code that reads `err.code` (the repo's convention) treats it as
 * try-again. It is NOT a `FunctionsError` instance (that class is not constructible from app code), so `instanceof FunctionsError` is false: match on `code`.
 */
export class AuthTokenUnavailableError extends Error {
  readonly code = 'functions/unavailable';
  constructor(cause?: unknown) {
    super('Your sign-in could not be refreshed, so this request was not sent. Check your connection and try again.');
    this.name = 'AuthTokenUnavailableError';
    if (cause !== undefined) (this as { cause?: unknown }).cause = cause;
  }
}

/** If authStateReady() has not settled by then (storage hang, blocked third-party storage) held calls are released and one warning is logged, so a stuck start-up cannot freeze every callable, public forms included. */
export const AUTH_READY_TIMEOUT_MS = 10_000;

/** null = nothing registered (not held); a promise = auth is still starting; resolved flag = ready (synchronous path). */
let waiting: Promise<void> | null = null;
let registered = false;
let tokens: TokenSource | null = null;

/**
 * Called once by config/firebase.ts. Starts waiting on the given function immediately.
 * @param {AuthReady} authReady resolves when Firebase Auth has finished restoring (or finding no) signed-in user.
 * @param {TokenSource} [tokenSource] when given, every call made while a user is signed in first fetches the ID token (see TokenSource).
 * @return {void}
 */
export function registerAuthReady(authReady: AuthReady, tokenSource?: TokenSource): void {
  registered = true;
  tokens = tokenSource ?? null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const ready = Promise.resolve().then(authReady).then(() => 'ready' as const);
  const timedOut = new Promise<'timeout'>((resolve) => {
    timer = setTimeout(() => resolve('timeout'), AUTH_READY_TIMEOUT_MS);
  });
  const promise: Promise<void> = Promise.race([ready, timedOut])
    .then(
      (outcome) => {
        if (outcome === 'timeout') console.warn(`[authReadyCallable] authStateReady() did not settle in ${AUTH_READY_TIMEOUT_MS} ms; releasing held callables`);
      },
      (err: unknown) => {
        // Release the held calls rather than hang them forever; the server will accept or refuse them as it always did.
        console.warn('[authReadyCallable] authStateReady() failed; releasing held callables', err);
      },
    )
    .then(() => {
      if (timer !== undefined) clearTimeout(timer);
      // Only clear if this registration is still the current one (a later registration replaced it).
      if (waiting === promise) waiting = null;
    });
  waiting = promise;
}

/** Test hook: forget any registration so a test starts from "nothing registered". */
export function __resetAuthReadyForTests(): void {
  waiting = null;
  registered = false;
  tokens = null;
}

/** @return {boolean} true once registerAuthReady has been called (config/firebase.ts did its part). */
export function isAuthReadyRegistered(): boolean {
  return registered;
}

/** @return {Promise<void> | null} a promise to wait on while auth is still starting, or null when calls may go straight out. */
function pending(): Promise<void> | null {
  return waiting;
}

/** Fetches the ID token (forced refresh on a retry). Any failure becomes a retryable refusal: nothing is sent without a token for a signed-in user. */
async function ensureToken(source: TokenSource, forceRefresh: boolean): Promise<void> {
  try {
    await source.getIdToken(forceRefresh);
  } catch (err) {
    throw new AuthTokenUnavailableError(err);
  }
}

const isUnauthenticated = (err: unknown): boolean => (err as { code?: unknown } | null)?.code === 'functions/unauthenticated';

/**
 * Treats an 'unauthenticated' with no `details.reason` as the SDK/gateway rejection of a call that carried no usable token (raised BEFORE any
 * handler runs). A handler can also throw 'unauthenticated' from INSIDE, after it has already written: the Drive-backed handlers do, and they tag it
 * with details.reason ('drive_credentials_rejected' / 'drive_oauth_expired'), so any reason present means "do not retry". NAMED LIMIT: a handler
 * that throws 'unauthenticated' from inside WITHOUT a reason is indistinguishable from a token rejection and WOULD be retried once; e.g. the Gmail
 * handlers' own connection-expired errors carry no reason today. Retried calls are therefore only safe where the handler checks credentials before
 * it writes; tagging those throws with a reason is the fix at the source.
 */
const isTokenRejection = (err: unknown): boolean =>
  isUnauthenticated(err) && (err as { details?: { reason?: unknown } | null } | null)?.details?.reason === undefined;

/**
 * One call with a token guaranteed first. On a token rejection it refreshes the token and retries ONCE, as the SAME user (if the signed-in user
 * changed meanwhile the original error is surfaced: a call started as one user is never re-sent as another). Anything else passes through untouched.
 */
async function callWithToken<T>(source: TokenSource, send: () => Promise<T>): Promise<T> {
  await ensureToken(source, false);
  const startedAs = source.uid();
  try {
    return await send();
  } catch (err) {
    if (!isTokenRejection(err) || !source.hasUser() || source.uid() !== startedAs) throw err;
    await ensureToken(source, true);
    // The forced refresh is async: the user may have switched while it ran. Never re-send as someone else.
    if (source.uid() !== startedAs) throw err;
    return send();
  }
}

function hold<Req, Res, Stream>(inner: HttpsCallable<Req, Res, Stream>, label: string): HttpsCallable<Req, Res, Stream> {
  // Signed in => the token path (always async). Signed out or nothing registered => the original path, synchronous once auth is ready.
  const source = (): TokenSource | null => (tokens && tokens.hasUser() ? tokens : null);
  // async/await, not `.catch` on the returned value: a test mock whose callable returns a plain value (not a promise) must keep working. The body of an
  // async function runs synchronously up to its first await, so the SDK call still goes out in the same tick on the no-wait path.
  const call = (async (...args: Parameters<HttpsCallable<Req, Res, Stream>>) => {
    const go = () => {
      const t = source();
      return t ? callWithToken(t, () => inner(...args)) : inner(...args);
    };
    const wait = pending();
    try {
      return await (wait ? wait.then(go) : go());
    } catch (err: unknown) {
      // OFFICEHUB-CLIENT-71: a rejection carries which callable failed and the device state (visibility, online) at that moment; see callableFailureContext.ts.
      throw annotateCallableFailure(err, label);
    }
  }) as HttpsCallable<Req, Res, Stream>;
  // The stream path gets the same token guarantee but no retry: a stream cannot be replayed once chunks have been delivered.
  call.stream = (...args: Parameters<HttpsCallable<Req, Res, Stream>['stream']>) => {
    const go = async () => {
      const t = source();
      if (t) await ensureToken(t, false);
      return inner.stream(...args);
    };
    const wait = pending();
    return wait ? wait.then(go) : go();
  };
  return call;
}

/** Same as the SDK's `httpsCallable`, but the call is held until Firebase Auth has finished starting. */
export function httpsCallable<RequestData = unknown, ResponseData = unknown, StreamData = unknown>(
  functionsInstance: Functions,
  name: string,
  ...rest: [options?: HttpsCallableOptions]
): HttpsCallable<RequestData, ResponseData, StreamData> {
  // `rest` is forwarded as given (never an explicit trailing undefined), so the SDK sees exactly the arguments the call site wrote.
  return hold(sdkHttpsCallable<RequestData, ResponseData, StreamData>(functionsInstance, name, ...rest), name);
}

/** Same as the SDK's `httpsCallableFromURL`, but the call is held until Firebase Auth has finished starting. */
export function httpsCallableFromURL<RequestData = unknown, ResponseData = unknown, StreamData = unknown>(
  functionsInstance: Functions,
  url: string,
  ...rest: [options?: HttpsCallableOptions]
): HttpsCallable<RequestData, ResponseData, StreamData> {
  // Named limit: for the IR router the label is the router service's name, not the logical callable (the action travels in the payload, which is never read here).
  return hold(sdkHttpsCallableFromURL<RequestData, ResponseData, StreamData>(functionsInstance, url, ...rest), url);
}
